/**
 * Workflow Orchestration Hook
 * 
 * Centralized automation layer that watches for state changes across modules
 * and triggers lightweight automations to keep the system connected:
 * 
 * - Job becomes "Ready" → flag for scheduling
 * - Job is scheduled → trigger warehouse prep visibility
 * - QA incomplete → prevent job completion
 * - High-severity issue logged → flag for management
 * - Credit issued → ensure it appears in reporting
 * - Materials status changes → sync readiness
 * - Rescission period ends → auto-clear for production
 * - Financial integrity → seed contractValue from payoutAmount ONLY when financials struct is first created (never overwrites canonical contractValue)
 * - Quote won → job created (handled in QuoteDetail)
 */

import { useEffect, useRef, useCallback } from "react";
import { useOptionalAppData } from "@/data/AppDataContext";
import type { Job, Issue, ContractorOpportunity, Contractor } from "@/data/types";
import { getInstallReadiness } from "@/components/JobWorkflowStatus";
import { getRescissionStatus } from "@/data/rescission";
import { getStageConfig, canModuleWriteStatus, isValidStatusValue, type StatusFieldKey } from "@/data/workflowEngine";
import { validateStatusWrite } from "@/data/workflowAudit";
import { JobService } from "@/services/job.service";
import { generateInvoicePlan } from "@/data/invoiceEngine";
import { isExternalManagedImport, migrationBypasses } from "@/data/import/migrationVisibilityGuard";
import {
  SUPABASE_SYNC_DEBUG,
  WORKFLOW_ORCHESTRATION_BOOT_PERSIST_DELAY_MS,
  WORKFLOW_ORCHESTRATION_PERSIST_ENABLED,
} from "@/lib/dataRuntimeFlags";

function computeEstimatedMaterialCost(job: Job): number {
  const coreMaterialsTotal = (job.materialsList ?? []).reduce((sum, row) => {
    const qty = Math.max(0, Number(row.quantityRequired ?? 0));
    const unit = Math.max(0, Number(row.unitCost ?? 0));
    const taxPercent = Math.max(0, Number(row.taxPercent ?? 0));
    return sum + (qty * unit * (1 + (taxPercent / 100)));
  }, 0);

  const fieldMaterialsSubtotal = (job.fieldMaterials ?? []).reduce((sum, row) => {
    const qty = Math.max(1, Number(row.quantity ?? 1) || 1);
    const unit = Math.max(0, Number(row.unitCost ?? 0));
    return sum + (qty * unit);
  }, 0);

  const seenReceiptIds = new Set<string>();
  const fieldMaterialReceiptTax = (job.fieldMaterials ?? []).reduce((sum, row) => {
    const receiptTax = (row.receipts ?? []).reduce((receiptSum, receipt) => {
      const attachmentId = String(receipt?.attachmentId ?? "").trim();
      const fileName = String(receipt?.fileName ?? "").trim().toLowerCase();
      const vendorName = String(receipt?.vendorName ?? "").trim().toLowerCase();
      const purchasedAt = String(receipt?.purchasedAt ?? "").trim();
      const total = typeof receipt?.total === "number" && Number.isFinite(receipt.total)
        ? receipt.total.toFixed(2)
        : "";
      const identity = attachmentId || [fileName || "no-file", vendorName || "no-vendor", purchasedAt || "no-date", total || "no-total"].join(":");
      if (!identity || seenReceiptIds.has(identity)) return receiptSum;
      seenReceiptIds.add(identity);
      return receiptSum + Math.max(0, Number(receipt?.tax ?? 0));
    }, 0);
    return sum + receiptTax;
  }, 0);

  return coreMaterialsTotal + fieldMaterialsSubtotal + fieldMaterialReceiptTax;
}

function computeSubcontractorPayout(
  job: Job,
  opportunities: ContractorOpportunity[],
  contractors: Contractor[],
): number {
  const jobOpps = opportunities.filter((opp) => opp.jobId === job.id);
  const winner = jobOpps.find((opp) => opp.isWinner && Number(opp.bidAmount || 0) > 0);
  if (winner) return Number(winner.bidAmount || 0);

  const accepted = jobOpps.find((opp) => opp.responseStatus === "Accepted" && Number(opp.bidAmount || 0) > 0);
  if (accepted) return Number(accepted.bidAmount || 0);

  if (job.assignedSubcontractor) {
    const assigned = jobOpps.find((opp) => {
      const contractor = contractors.find((c) => c.id === opp.contractorId);
      return contractor?.companyName === job.assignedSubcontractor && Number(opp.bidAmount || 0) > 0;
    });
    if (assigned) return Number(assigned.bidAmount || 0);
  }

  return 0;
}

function deriveDbBackedPaymentStatus(job: Job): Job["financials"]["paymentStatus"] {
  const fin = job.financials;
  if (!fin) return "Not Invoiced";
  const contractValue = Math.max(0, Number(fin.contractValue ?? job.payoutAmount ?? 0));
  const invoices = fin.invoices ?? [];
  const payments = fin.payments ?? [];
  const normalize = (value: unknown) => String(value ?? "").trim().toLowerCase();

  const paidFromPayments = payments.reduce((sum, p) => sum + Math.max(0, Number(p.amount ?? 0)), 0);
  const paidFromInvoices = invoices.reduce((sum, inv) => {
    if (normalize(inv.status) !== "paid") return sum;
    return sum + Math.max(0, Number(inv.amount ?? 0));
  }, 0);
  const resolvedPaid = Math.max(paidFromPayments, paidFromInvoices);

  if (contractValue > 0 && resolvedPaid >= contractValue) return "Paid in Full";
  if (invoices.some((inv) => normalize(inv.status) === "overdue")) return "Overdue";
  if (invoices.length === 0) return "Not Invoiced";
  if (resolvedPaid > 0) return "Partial Payment";

  const hasDeposit = invoices.some((inv) => normalize(inv.type) === "deposit");
  return hasDeposit ? "Deposit Requested" : "Invoiced";
}

/** Log ownership violations in dev mode */
function logOwnershipViolation(jobId: string, field: StatusFieldKey, value: string, callerModule: string) {
  const result = validateStatusWrite(callerModule, field, value);
  if (!result.valid && import.meta.env.DEV) {
    console.warn(`[WorkflowOwnership] Job ${jobId}: ${result.violation}`);
  }
}

/** Derive what the materialReadiness SHOULD be based on current job data */
function deriveMaterialReadiness(job: Job): Job["materialReadiness"] {
  if (["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(job.materialReadiness ?? "").trim())) {
    return job.materialReadiness;
  }
  if (job.materialsList && job.materialsList.length > 0) {
    const allStaged = job.materialsList.every(m => m.quantityStaged >= m.quantityRequired);
    const allReceived = job.materialsList.every(m => m.quantityAllocated >= m.quantityRequired);
    const hasMissing = job.materialsList.some(m => m.missingItems > 0);
    if (allStaged) return "Staged for Job";
    if (allReceived) return "Fully Received";
    if (hasMissing) return "Missing Items";
    return "Ordered";
  }
  return job.materialReadiness;
}

/** Derive deprecated orderStatus from planningStatus */
function deriveOrderStatus(job: Job): Job["orderStatus"] {
  switch (job.planningStatus) {
    case "Ordered": return "Ordered";
    case "Awaiting Delivery": return "ETA Received";
    case "Receiving":
    case "Staged":
    case "Ready for Install": return "Confirmed";
    default:
      if (job.materialReadiness === "Ordered") return "Ordered";
      if (job.materialReadiness === "Fully Received" || job.materialReadiness === "Staged for Job" || job.materialReadiness === "Loaded for Crew") return "Confirmed";
      return "Not Ordered";
  }
}

/** Derive deprecated materialsStatus from materialReadiness */
function deriveMaterialsStatus(job: Job): Job["materialsStatus"] {
  switch (job.materialReadiness) {
    case "Fully Received":
    case "Staged for Job":
    case "Loaded for Crew": return "Ready";
    case "Missing Items":
    case "Field Purchase Required": return "Missing Items";
    default: return "Not Ready";
  }
}

/** Derive holdStatus — clear when no longer in revision */
function deriveHoldStatus(job: Job): { holdStatus?: string; holdReason?: string } {
  if (!job.holdStatus) return {};
  // If job has been approved or moved past review, clear hold
  if (job.intakeApproved || job.planningStatus === "In Planning" || job.planningStatus === "Ready for Ordering" || job.planningStatus === "Ordered") {
    return { holdStatus: undefined, holdReason: undefined };
  }
  return { holdStatus: job.holdStatus, holdReason: job.holdReason };
}

/** Determine if a job's dispatch status should be updated based on readiness */
function deriveDispatchStatus(
  job: Job,
  openIssues: Issue[],
  buildReviewEvents: import("@/data/buildReviewTypes").BuildReviewEvent[] = [],
): Job["dispatchStatus"] {
  // Don't override certain statuses
  if (["Assigned", "Accepted", "Rejected", "Completed"].includes(job.dispatchStatus)) {
    return job.dispatchStatus;
  }

  const readiness = getInstallReadiness(job, openIssues, buildReviewEvents);
  if (readiness.status === "Ready for Install" && job.assignmentType !== "Unassigned") {
    return "Ready for Dispatch";
  }
  if (readiness.status === "Not Ready") {
    return "Not Ready";
  }
  return job.dispatchStatus;
}

/** Check if job stage should auto-advance based on current state */
function deriveJobStage(job: Job, openIssues: Issue[]): Job["currentStage"] {
  const externalManaged = isExternalManagedImport(job);
  // Never regress completed jobs, except imported external-managed rows
  // that were prematurely marked completed before QA pass.
  if (job.currentStage === "Completed") {
    if (externalManaged && job.qaStatus !== "Passed") return "QA Review";
    return "Completed";
  }

  const current = job.currentStage;
  const permitOk = migrationBypasses(job, "permitting") || !job.permitRequired || job.permitStatus === "Approved";
  const materialsReady = ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness);

  // ── Build Review approval ──────────────────────────────────────────────────
  // Review and Validation → Review Complete  (build review approved)
  if (current === "Review and Validation" && job.intakeApproved) {
    return "Review Complete";
  }

  // Review Complete → Ready for Ordering  (approved for material ordering)
  if (current === "Review Complete" && job.approvedForOrdering) {
    return "Ready for Ordering";
  }

  // ── Ordering module updates planningStatus ─────────────────────────────────
  // Ready for Ordering → Ordered  (procurement has placed the order)
  if (
    current === "Ready for Ordering" &&
    ["Ordered", "Awaiting Delivery", "Receiving", "Staged", "Ready for Install"].includes(
      job.planningStatus ?? ""
    )
  ) {
    return "Ordered";
  }

  // ── Permit + Materials gate ────────────────────────────────────────────────
  // Ordered → Ready for Scheduling  (both permit and materials cleared)
  if (current === "Ordered" && permitOk && materialsReady) {
    return "Ready for Scheduling";
  }

  // Ordered → Permit and Order Pending  (still waiting on permit or materials)
  if (current === "Ordered" && (!permitOk || !materialsReady)) {
    return "Permit and Order Pending";
  }

  // Permit and Order Pending → Ready for Scheduling  (both cleared)
  if (current === "Permit and Order Pending" && permitOk && materialsReady) {
    return "Ready for Scheduling";
  }

  // ── Scheduling ─────────────────────────────────────────────────────────────
  // Ready for Scheduling → Scheduled Tentative  (install date has been set)
  if (current === "Ready for Scheduling" && job.scheduledInstallDate) {
    return "Scheduled Tentative";
  }

  // Scheduled Tentative → Scheduled Confirmed  (scheduling module confirms)
  if (current === "Scheduled Tentative" && job.schedulingStatus === "Confirmed") {
    return "Scheduled Confirmed";
  }

  // Keep Scheduled Confirmed sticky.
  // Dispatch/assignment actions should advance from here explicitly;
  // do not auto-regress/flip stage in orchestration.
  if (current === "Scheduled Confirmed") {
    return "Scheduled Confirmed";
  }

  // ── Dispatch & Assignment ──────────────────────────────────────────────────
  // Ready for Dispatch / Available for Assignment → Assigned and Dispatched
  if (
    (current === "Ready for Dispatch" || current === "Available for Contractor Assignment") &&
    job.assignmentType !== "Unassigned" &&
    (job.dispatchStatus === "Assigned" || job.dispatchStatus === "Accepted")
  ) {
    return "Assigned and Dispatched";
  }

  // ── Install execution ──────────────────────────────────────────────────────
  // Assigned and Dispatched → In Progress  (installer marks work started)
  if (
    current === "Assigned and Dispatched" &&
    (job.installStatus === "In Progress" || job.installStatus === "On Site")
  ) {
    return "In Progress";
  }

  // In Progress → QA Review  (installer submits QA form)
  if (current === "In Progress" && job.qaSubmitted) {
    return "QA Review";
  }

  // ── QA & Closeout ──────────────────────────────────────────────────────────
  // QA Review → Completed  (QA passed, zero open issues)
  if (
    current === "QA Review" &&
    job.qaSubmitted &&
    job.qaStatus === "Passed" &&
    openIssues.length === 0 &&
    !externalManaged
  ) {
    return "Completed";
  }

  return current;
}

/** Derive install status from current stage */
function deriveInstallStatus(job: Job): Job["installStatus"] {
  if (job.installStatus === "Install Complete") return "Install Complete";
  if (job.qaSubmitted) return "Install Complete";
  if (job.currentStage === "In Progress") return "In Progress";
  if (job.currentStage === "Assigned and Dispatched") return "En Route";
  if (job.currentStage === "Scheduled Confirmed" || job.currentStage === "Scheduled Tentative") return "Scheduled";
  return job.installStatus || "Not Started";
}

/** Derive QA status from job state — only after install is complete */
function deriveQaStatus(job: Job, openIssues: Issue[]): Job["qaStatus"] {
  if (job.qaStatus === "Passed") return "Passed";
  if (job.qaSubmitted && openIssues.length === 0) return "Passed";
  if (job.qaSubmitted) return "Submitted";
  if (job.installStatus === "Install Complete" || job.currentStage === "In Progress") return "Not Started";
  return job.qaStatus || "Not Started";
}

/** Derive scheduling status — only allow scheduling when ready */
function deriveSchedulingStatus(job: Job): Job["schedulingStatus"] {
  if (job.schedulingStatus === "Confirmed") return "Confirmed";
  if (job.schedulingStatus === "Crew Assigned") return "Crew Assigned";
  if (job.schedulingStatus === "Scheduled") return "Scheduled";
  // Only mark Unscheduled if materials are ready
  if (job.installReadiness === "Ready" && !job.scheduledInstallDate) return "Unscheduled";
  if (!job.installReadiness || job.installReadiness === "Not Ready") return "Not Ready";
  return job.schedulingStatus || "Not Ready";
}

/** Sync closeout status based on job state */
function deriveCloseoutStatus(job: Job, openIssues: Issue[]): Job["closeoutStatus"] {
  if (job.closeoutStatus === "Closed") return "Closed";
  
  if (job.qaSubmitted && job.currentStage === "QA Review") {
    if (openIssues.length === 0 && job.closeoutStatus === "Not Started") {
      return "QA Passed";
    }
  }
  
  return job.closeoutStatus;
}

/** Auto-clear rescission when period has ended */
function deriveRescissionStatus(job: Job): Job["rescissionStatus"] {
  if (!job.rescissionEndsAt) return job.rescissionStatus;
  if (job.rescissionStatus === "In Rescission") {
    const computedStatus = getRescissionStatus(job.rescissionEndsAt);
    if (computedStatus === "Cleared for Production") {
      return "Cleared for Production";
    }
  }
  return job.rescissionStatus;
}

/** Sync financials metadata only. Invoice creation is DB-backed in JobFinancialsPanel. */
function syncFinancials(
  job: Job,
  opportunities: ContractorOpportunity[],
  contractors: Contractor[],
): Job["financials"] {
  if (!job.financials) {
    // No financials struct yet — only create one for post-sale jobs
    const postSaleStages = ["Sale Closed", "Review Complete", "Ready for Ordering", "Ordered", "Receiving", "Staged", "Ready for Install", "Scheduled Tentative", "Scheduled Confirmed", "Completed", "QA Passed", "QA Review"];
    if (!postSaleStages.includes(job.currentStage)) return job.financials;

    // Build a minimal financials struct with plan only (no local invoice stubs).
    const plan = generateInvoicePlan(job);
    const estimatedMaterialCost = computeEstimatedMaterialCost(job);
    const subcontractorPayout = computeSubcontractorPayout(job, opportunities, contractors);
    const estimatedLaborCost = subcontractorPayout > 0 ? subcontractorPayout : 0;
    const marginEstimate = job.payoutAmount - estimatedMaterialCost - estimatedLaborCost;
    return {
      contractValue: job.payoutAmount,
      estimatedMaterialCost,
      estimatedLaborCost,
      subcontractorPayout,
      marginEstimate,
      paymentStatus: "Deposit Requested" as const,
      payoutStatus: "Not Submitted" as const,
      invoicePlan: plan,
      invoices: [],
      payments: [],
    };
  }

  const fin = job.financials;
  let changed = false;
  // CANONICAL: Job.financials.contractValue is the source of truth for customer
  // contract value. Never overwrite it from job.payoutAmount (which is the
  // contractor install payout — a different concept). Change orders update
  // contractValue directly via the change-order flow. See mem://finance/canonical-pricing.
  const contractValue = fin.contractValue;
  let invoicePlan = fin.invoicePlan;
  let estimatedMaterialCost = Number(fin.estimatedMaterialCost || 0);
  let estimatedLaborCost = Number(fin.estimatedLaborCost || 0);
  let subcontractorPayout = Number(fin.subcontractorPayout || 0);
  let marginEstimate = Number(fin.marginEstimate || 0);
  let paymentStatus = fin.paymentStatus;

  // Auto-generate invoice plan if missing
  if (!invoicePlan) {
    invoicePlan = generateInvoicePlan(job);
    changed = true;
  }

  const computedMaterialCost = computeEstimatedMaterialCost(job);
  if (Math.abs(computedMaterialCost - estimatedMaterialCost) > 0.009) {
    estimatedMaterialCost = computedMaterialCost;
    changed = true;
  }

  const computedSubPayout = computeSubcontractorPayout(job, opportunities, contractors);
  if (Math.abs(computedSubPayout - subcontractorPayout) > 0.009) {
    subcontractorPayout = computedSubPayout;
    changed = true;
  }

  // For subcontractor jobs, labor cost should reflect payout unless explicitly higher.
  const computedLaborCost = subcontractorPayout > 0 ? Math.max(estimatedLaborCost, subcontractorPayout) : estimatedLaborCost;
  if (Math.abs(computedLaborCost - estimatedLaborCost) > 0.009) {
    estimatedLaborCost = computedLaborCost;
    changed = true;
  }

  const computedMargin = contractValue - estimatedMaterialCost - estimatedLaborCost;
  if (Math.abs(computedMargin - marginEstimate) > 0.009) {
    marginEstimate = computedMargin;
    changed = true;
  }

  const computedPaymentStatus = deriveDbBackedPaymentStatus({
    ...job,
    financials: {
      ...fin,
      contractValue,
      estimatedMaterialCost,
      estimatedLaborCost,
      subcontractorPayout,
      marginEstimate,
      invoicePlan,
    },
  });
  if (computedPaymentStatus !== paymentStatus) {
    paymentStatus = computedPaymentStatus;
    changed = true;
  }

  if (!changed) return job.financials;

  return {
    ...fin,
    contractValue,
    estimatedMaterialCost,
    estimatedLaborCost,
    subcontractorPayout,
    marginEstimate,
    paymentStatus,
    invoicePlan,
  };
}

/** Auto-escalate urgent issues open > 3 days */
function shouldEscalateIssue(issue: Issue): boolean {
  if (issue.status !== "Open" || issue.severity !== "Urgent") return false;
  const created = new Date(issue.dateCreated);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - created.getTime()) / 86400000);
  return diffDays > 3;
}

export function useWorkflowOrchestration() {
  const appData = useOptionalAppData();
  const jobs = appData?.jobs ?? [];
  const issues = appData?.issues ?? [];
  const opportunities = appData?.opportunities ?? [];
  const contractors = appData?.contractors ?? [];
  const setJobs = appData?.setJobs;
  const hydrationReady = Boolean(appData) &&
    !appData?.jobsLoading &&
    !appData?.issuesLoading &&
    !appData?.contractorsLoading;
  const lastRunRef = useRef<string>("");
  const hydrationReadyAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!appData) {
      hydrationReadyAtRef.current = null;
      return;
    }
    if (hydrationReady && hydrationReadyAtRef.current === null) {
      hydrationReadyAtRef.current = Date.now();
    }
  }, [appData, hydrationReady]);

  const runOrchestration = useCallback(() => {
    if (!setJobs) return;
    // Create a fingerprint to avoid infinite loops
    const fingerprint = JSON.stringify({
      jobStages: jobs.map(j =>
        [
          j.id,
          j.currentStage,
          j.materialReadiness,
          j.dispatchStatus,
          j.closeoutStatus,
          j.rescissionStatus,
          j.payoutAmount,
          j.installStatus,
          j.qaStatus,
          j.qaSubmitted,
          j.schedulingStatus,
          j.scheduledInstallDate,
          j.planningStatus,
          j.permitStatus,
          j.intakeApproved,
          j.approvedForOrdering,
          j.assignmentType,
          j.financials?.invoices?.length ?? 0,
          j.financials?.payments?.length ?? 0,
          j.materialsList?.length ?? 0,
        ].join(":")
      ),
      oppSignals: opportunities.map((o) => [o.id, o.jobId, o.responseStatus, o.bidAmount ?? 0, o.isWinner ? 1 : 0].join(":")),
      issueEscalations: issues.filter(i => i.severity === "Urgent" && i.status === "Open").map(i => i.id),
    });
    
    if (fingerprint === lastRunRef.current) return;
    lastRunRef.current = fingerprint;

    let jobsUpdated = false;
    const updatedJobs = jobs.map(job => {
      if (job.currentStage === "Completed") return job;
      
      const openIssues = issues.filter(
        i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress")
      );

      const newMaterialReadiness = deriveMaterialReadiness(job);
      const newDispatchStatus = deriveDispatchStatus(job, openIssues);
      const newStage = deriveJobStage(job, openIssues);
      const newCloseoutStatus = deriveCloseoutStatus(job, openIssues);
      const newRescissionStatus = deriveRescissionStatus(job);
      const newFinancials = syncFinancials(job, opportunities, contractors);
      const newInstallStatus = deriveInstallStatus(job);
      const newQaStatus = deriveQaStatus(job, openIssues);
      const newSchedulingStatus = deriveSchedulingStatus(job);
      
      // Sync deprecated fields from canonical sources
      const newOrderStatus = deriveOrderStatus(job);
      const newMaterialsStatus = deriveMaterialsStatus(job);
      const holdUpdate = deriveHoldStatus(job);
      
      // Sync issue flag
      const hasOpenIssues = openIssues.length > 0;
      
      if (
        newMaterialReadiness !== job.materialReadiness ||
        newDispatchStatus !== job.dispatchStatus ||
        newStage !== job.currentStage ||
        newCloseoutStatus !== job.closeoutStatus ||
        newRescissionStatus !== job.rescissionStatus ||
        newFinancials !== job.financials ||
        hasOpenIssues !== job.issueFlag ||
        newOrderStatus !== job.orderStatus ||
        newMaterialsStatus !== job.materialsStatus ||
        holdUpdate.holdStatus !== job.holdStatus ||
        newInstallStatus !== job.installStatus ||
        newQaStatus !== job.qaStatus ||
        newSchedulingStatus !== job.schedulingStatus
      ) {
        jobsUpdated = true;

        // Validate ownership — log violations in dev mode
        if (newMaterialReadiness !== job.materialReadiness) logOwnershipViolation(job.id, "materialReadiness", newMaterialReadiness ?? "", "workflow_engine");
        if (newDispatchStatus !== job.dispatchStatus) logOwnershipViolation(job.id, "dispatchStatus", newDispatchStatus, "workflow_engine");
        if (newStage !== job.currentStage) logOwnershipViolation(job.id, "currentStage", newStage, "workflow_engine");
        if (newCloseoutStatus !== job.closeoutStatus) logOwnershipViolation(job.id, "closeoutStatus", newCloseoutStatus ?? "", "workflow_engine");
        if (newInstallStatus !== job.installStatus) logOwnershipViolation(job.id, "installStatus", newInstallStatus ?? "", "workflow_engine");
        if (newQaStatus !== job.qaStatus) logOwnershipViolation(job.id, "qaStatus", newQaStatus ?? "", "workflow_engine");
        if (newSchedulingStatus !== job.schedulingStatus) logOwnershipViolation(job.id, "schedulingStatus", newSchedulingStatus ?? "", "workflow_engine");

        // Persist orchestration mutations in one write per job to avoid
        // optimistic-concurrency conflicts between parallel stage/financial writes.
        const persistencePatch: Partial<Job> = {};
        if (newStage !== job.currentStage) {
          persistencePatch.currentStage = newStage;
        }
        if (newFinancials !== job.financials) {
          persistencePatch.financials = newFinancials;
        }
        if (Object.keys(persistencePatch).length > 0) {
          const hydrationReadyAt = hydrationReadyAtRef.current;
          const canPersistDerivedCorrections =
            WORKFLOW_ORCHESTRATION_PERSIST_ENABLED &&
            hydrationReadyAt !== null &&
            Date.now() - hydrationReadyAt >= WORKFLOW_ORCHESTRATION_BOOT_PERSIST_DELAY_MS;

          if (canPersistDerivedCorrections) {
            void (async () => {
              const { error } = await JobService.update(job.id, persistencePatch);
              if (!error) return;
              console.error(
                `[WorkflowOrchestration] Failed to persist orchestration sync for ${job.id}`,
                error,
              );
            })();
          } else if (SUPABASE_SYNC_DEBUG) {
            console.debug("[WorkflowOrchestration] Suppressed boot-time persistence", {
              jobId: job.id,
              fields: Object.keys(persistencePatch),
              hydrationReadyAt,
              delayMs: WORKFLOW_ORCHESTRATION_BOOT_PERSIST_DELAY_MS,
            });
          }
        }

        return {
          ...job,
          materialReadiness: newMaterialReadiness,
          dispatchStatus: newDispatchStatus,
          currentStage: newStage,
          closeoutStatus: newCloseoutStatus,
          rescissionStatus: newRescissionStatus,
          financials: newFinancials,
          issueFlag: hasOpenIssues,
          orderStatus: newOrderStatus,
          materialsStatus: newMaterialsStatus,
          installStatus: newInstallStatus,
          qaStatus: newQaStatus,
          schedulingStatus: newSchedulingStatus,
          ...holdUpdate,
        };
      }
      return job;
    });

    if (jobsUpdated) {
      setJobs(updatedJobs);
    }

    // Auto-escalate stale urgent issues — flag related jobs
    const escalatedJobIds = new Set<string>();
    issues.forEach(issue => {
      if (shouldEscalateIssue(issue)) {
        escalatedJobIds.add(issue.jobId);
      }
    });
  }, [jobs, issues, opportunities, contractors, setJobs]);

  useEffect(() => {
    if (!setJobs) return;
    runOrchestration();
  }, [runOrchestration, setJobs]);
}
